// This program has been developed by students from the bachelor Computer Science at
// Utrecht University within the Software Project course.
// © Copyright Utrecht University (Department of Information and Computing Sciences)

import React, { useContext } from 'react'
import { useCheckedState } from './CheckedContext'
import { useData } from './DataContext'
import config from '../../config/config'

// Create a new React context for the selected medicines data
// The application uses this data to visualize the selected datapoints in the table and charts
// Create a new React context for the selected medicines data
export const SelectedContext = React.createContext()

// Function that returns the context such that the data and update functions can be used
export function useSelectedData() {
  return useContext(SelectedContext)
}

/**
 * Provider component that provides the selected medicines data and update logic.
 * Note: Assumes config.data.primary_key is 'eu_pnumber'.
 */
export function SelectedProvider({ children }) {
  // We need the data and the current checked state and its setter
  const allData = useData()
  // Assuming useCheckedState provides setCheckedState to modify the selection
  const { checkedState, setCheckedState } = useCheckedState()

  // Initialize the state with the selected datapoints
  const selectedData = allData.filter((item) => {
    return checkedState[item[config.data.primary_key]]
  })

  /**
   * Updates the checkedState based on a list of primary keys (eu_pnumber).
   * This is used for adding pasted identifiers to the selection.
   * @param {string[]} primaryKeys - Array of unique eu_pnumber identifiers to include.
   * @param {'add'|'new'} mode - 'add' to merge with existing selection, 'new' to replace.
   */
  const updateSelection = (primaryKeys, mode) => {
    // Only proceed if setCheckedState is available
    if (!setCheckedState) {
        console.error("setCheckedState is not available in CheckedContext. Cannot update selection.");
        return;
    }
    
    setCheckedState((prevCheckedState) => {
      let nextCheckedState = { ...prevCheckedState };

      if (mode === 'new') {
        // Clear previous selection for 'Make new selection'
        nextCheckedState = {};
      }

      // Add the new primary keys to the checked state map
      primaryKeys.forEach(key => {
        nextCheckedState[key] = true;
      });

      return nextCheckedState;
    });
  };

  // The context value now provides both the data array and the update function
  const contextValue = {
    selectedData,
    updateSelection,
  }

  return (
    <SelectedContext.Provider value={contextValue}>
      {children}
    </SelectedContext.Provider>
  )
}
